import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { Organization } from "@/types/organizations";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Pencil, Activity, BarChart3, Settings, UsersIcon,
  Building, MapPinIcon, FileText, Store, ShoppingBag,
  Globe, Receipt, Building2, Trophy
} from "lucide-react";
import { EmptyState } from "@/components/ui/LoadingStates";
import SettingsAndFeaturesTab from "@/components/SettingsAndFeaturesTab";
import EmployeesTab from "@/components/corporate/organizations/EmployeesTab";
import { DepartmentsTab } from "@/components/corporate/organizations/DepartmentsTab";
import { CompaniesTab } from "@/components/corporate/organizations/CompaniesTab";
import LocationsTab from "@/components/corporate/organizations/LocationsTab";
import BookingsTab from "@/components/BookingsTab";
import VendorsTab from "@/components/VendorsTab";
import EditOrganization from "@/components/corporate/organizations/EditOrganization";
import MarketplacePage from "@/pages/MarketplaceAdmin/MarketplacePage";
import { OrganizationLocalizationPage } from "@/components/corporate/organizations/OrganizationLocalizationPage";
import { TournamentsTab } from "@/components/corporate/organizations/TournamentsTab";
import { OrganizationBrandingCard } from "@/components/corporate/organizations/OrganizationBrandingCard";
import { useOrgEntraSyncStatus, entraIntegrationLabel } from "@/hooks/useOrgEntraSyncStatus";


export default function ClientDetailPage() {
  const { organizationId } = useParams();
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  // Entra sign-in comes from entra-sync-status (the organization payload never carried it).
  const entraSyncQuery = useOrgEntraSyncStatus(organizationId);

  // Fetch organization details
  const { data: organization, isLoading: organizationLoading } = useQuery<Organization>({
    queryKey: [`/organizations/${organizationId}`],
    enabled: !!organizationId
  });

  const getStatusColor = (isActive: boolean) => {
    return isActive 
      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
      : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
  };

  if (organizationLoading || !organization) {
    return (
      <div className="container mx-auto py-8 px-4 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-8"></div>
        <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/organizations")}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div className="flex items-center gap-3">
            {organization.logo?.url ? (
              <img
                src={organization.logo.url}
                alt={organization.name}
                className="h-10 w-10 rounded-lg object-cover"
              />
            ) : (
              <div className="h-10 w-10 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                <Building className="h-5 w-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-semibold">{organization.name}</h1>
              <p className="text-sm text-muted-foreground" data-testid="org-portal-address">{organization.subdomain}.portal.athlyn.app</p>
            </div>
          </div>
          <Badge className={getStatusColor(organization.isActive)}>
            {organization.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Organization
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Billing & Usage
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <UsersIcon className="h-4 w-4" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="companies" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Companies
          </TabsTrigger>
          <TabsTrigger value="departments" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Departments
          </TabsTrigger>
          <TabsTrigger value="locations" className="flex items-center gap-2">
            <MapPinIcon className="h-4 w-4" />
            Locations
          </TabsTrigger>
          <TabsTrigger value="bookings" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Bookings
          </TabsTrigger>
          <TabsTrigger value="marketplace" className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            Marketplace
          </TabsTrigger>
          <TabsTrigger value="vendors" className="flex items-center gap-2">
            <Store className="h-4 w-4" />
            Vendors
          </TabsTrigger>
          <TabsTrigger value="tournaments" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Tournaments
          </TabsTrigger>
          <TabsTrigger value="localization" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Localization
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings & Features
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Organization Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Organization ID</span>
                  <span className="text-sm font-medium">{organization.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Subdomain</span>
                  <span className="text-sm font-medium">{organization.subdomain}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Country</span>
                  <span className="text-sm font-medium">{organization.country}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Default Language</span>
                  <span className="text-sm font-medium">{organization.defaultLanguage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Entra Integration</span>
                  <span className="text-sm font-medium">
                    {entraIntegrationLabel(entraSyncQuery)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">White Label</span>
                  <Badge variant={organization.isWhiteLabel ? "default" : "secondary"} className="text-xs">
                    {organization.isWhiteLabel ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Organization Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Created</span>
                  <span className="text-sm font-medium">
                    {new Date(organization.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Last Updated</span>
                  <span className="text-sm font-medium">
                    {new Date(organization.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className="text-sm font-medium">
                    {organization.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <OrganizationBrandingCard organization={organization} />
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <EmptyState
            icon={BarChart3}
            title="Analytics not available yet"
            description="Per-organization analytics are not connected to a backend endpoint yet."
          />
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          <EmptyState
            icon={Receipt}
            title="Billing & usage not available yet"
            description="Per-organization billing and usage are not connected to a backend endpoint yet."
          />
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-6">
          <EmployeesTab organizationId={organizationId!} />
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="space-y-6">
          <CompaniesTab organizationId={organizationId!} />
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-6">
          <DepartmentsTab organizationId={organizationId!} />
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-6">
          <LocationsTab organizationId={organizationId!} />
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings" className="space-y-6">
          <BookingsTab organizationId={organizationId!} />
        </TabsContent>

        {/* Marketplace Tab */}
        <TabsContent value="marketplace" className="space-y-6">
          <div className="h-full">
            <MarketplacePage />
          </div>
        </TabsContent>

        {/* Vendor Assignments Tab */}
        <TabsContent value="vendors" className="space-y-6">
          <VendorsTab />
        </TabsContent>


        {/* Tournaments Tab */}
        <TabsContent value="tournaments" className="space-y-6">
          <TournamentsTab organizationId={organizationId!} />
        </TabsContent>

        {/* Localization Tab */}
        <TabsContent value="localization" className="space-y-6">
          <OrganizationLocalizationPage organizationId={organizationId!} />
        </TabsContent>

        {/* Settings & Features Tab */}
        <TabsContent value="settings" className="space-y-6">
          <SettingsAndFeaturesTab organizationId={organizationId!} />
        </TabsContent>
      </Tabs>

      {/* Edit Organization Modal */}
      <EditOrganization
        organization={organization}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
      />
    </div>
  );
}